import { ReactNode, useState, useEffect } from 'react';

import { useToast } from '@/hooks/useToast.hook';

interface ErrorBoundaryProps {
  children: ReactNode;
}

export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const toast = useToast();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setHasError(true);
      setError(new Error(event.error?.message || 'Unknown error occurred'));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setHasError(true);
      setError(
        new Error(event.reason?.message || 'Promise rejection occurred'),
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  // Reset error boundary when children change
  useEffect(() => {
    if (hasError) {
      setHasError(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <>
      {children}
      {hasError &&
        toast(error?.message || 'An unexpected error occurred', 'error', {
          duration: 5000,
          isClosable: true,
          onCloseComplete: () => {
            setHasError(false);
            setError(null);
          },
        })}
    </>
  );
};
